import { ServiceResponse } from '@nx-smartmonkey/shared/hooks';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { driverService } from '../../application/DriverService';
import { SelectionActionType } from '../../context/selection/actions';
import { useSelectionContext } from '../../context/selection/context';
import { Driver } from '../../domain/driver/Driver';

import useErrorMiddleware from '../errorMiddleware/useErrorMiddleware';
import { useRetrieveActivePlan } from '../plan/useRetrieveActivePlan';
import { useRetrieveActiveProject } from '../project/useRetrieveActiveProject';
import { useShowSnackbarMessage } from '../snackbarMessage/useShowSnackbarMessage';

type DeleteDriversParams = { driverIds: string[] };

type UseDeleteDriversProps = ServiceResponse<({ driverIds }: DeleteDriversParams) => void, void>;

// Hooks act as 'Adapter' layer.
export const useDeleteDrivers = (): UseDeleteDriversProps => {
  const queryClient = useQueryClient();
  const { data: activeProject } = useRetrieveActiveProject();
  const { data: activePlan } = useRetrieveActivePlan();
  const { selectionState, selectionDispatch } = useSelectionContext();
  const { sessionExpiredLogout } = useErrorMiddleware();
  const { showSnackbarMessage } = useShowSnackbarMessage();

  const { isLoading, isSuccess, isError, mutate } = useMutation<Driver[], unknown, DeleteDriversParams>(
    ({ driverIds }: DeleteDriversParams): Promise<Driver[]> =>
      driverService.deleteDrivers({ driverIds, project: activeProject! }),
    {
      onSuccess: async (deletedDrivers: Driver[]) => {
        const deletedDriversIds = deletedDrivers.map((deletedDriver) => deletedDriver.id);

        deletedDriversIds.map(async (deletedDriversId) => {
          await queryClient.invalidateQueries([`planner_driver`, deletedDriversId]);
        });

        await queryClient.invalidateQueries([`planner_active_plan_drivers`, activePlan?.id]);

        if (deletedDriversIds.some((deletedDriversId) => selectionState.selectedDrivers.includes(deletedDriversId))) {
          selectionDispatch({
            type: SelectionActionType.SET_ACTIVE_PLAN_SELECTED_DRIVERS,
            payload: {
              selectedDrivers: selectionState.selectedDrivers.filter(
                (selectedDriver) => !deletedDriversIds.includes(selectedDriver)
              ),
            },
          });
        }

        showSnackbarMessage({
          type: `success`,
          messageIntlId: `infoerror.deleteroutesuccess`,
        });
      },
      // If the mutation fails, use the context we returned above
      onError: (err: any, variables: DeleteDriversParams, context: any) => {
        console.info(`Delete Stops Error: `, err?.response);

        if (err?.response?.status === 401) {
          sessionExpiredLogout();
          return;
        }
        showSnackbarMessage({
          messageIntlId: `infoerror.${err?.response?.data?.messageId ?? `network_error`}`,
        });
      },
    }
  );

  return {
    error: isError,
    isLoading: isLoading,
    success: isSuccess,
    run: ({ driverIds }: DeleteDriversParams) => mutate({ driverIds }),
  };
};
